import React, { ReactElement } from 'react';

import { m as motion } from 'framer-motion';
import { AnimationControls } from 'framer-motion/types/animation/types';
import styled from 'styled-components';

import { iconsPath } from 'chime-ui/components/Icons';
import { Title } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system/colors';
import media from 'chime-ui/system/media';

import {
  hand1Variants,
  hand2Variants,
  hand3Variants,
  hand4Variants,
  hand5Variants,
  star1Variants,
  star2Variants,
  welcomeMessageVariants,
  welcomeTitleVariants,
} from 'components/SubPages/Landing/ReturnUser/ReturnUseLanding.animation';

type WelcomeMessageProps = {
  animationControls: AnimationControls;
  firstName: string;
};

function WelcomeMessage({
  animationControls,
  firstName,
}: WelcomeMessageProps): ReactElement {
  return (
    <WelcomeMessageLayout
      animate={animationControls}
      variants={welcomeMessageVariants}
    >
      <WelcomeMessageContentLayout>
        <TitleLayout
          animate={animationControls}
          variants={welcomeTitleVariants}
        >
          <Title size={'medium'}>
            <span role={'img'} aria-label={'money-mouth face'}>
              👀
            </span>
          </Title>
          <Title size={'medium'}>Look Everyone!</Title>
          <Title size={'medium'}>{firstName} is Back!</Title>
        </TitleLayout>
        <Star1Layout animate={animationControls} variants={star1Variants}>
          <img alt={'star 1'} src={`${iconsPath}star 1.svg`} />
        </Star1Layout>
        <Star2Layout animate={animationControls} variants={star2Variants}>
          <img alt={'star 2'} src={`${iconsPath}star 2.svg`} />
        </Star2Layout>
        <Hand1Layout animate={animationControls} variants={hand1Variants}>
          <img alt={'Hand 1'} src={`${iconsPath}hand 1.svg`} />
        </Hand1Layout>
        <Hand2Layout animate={animationControls} variants={hand2Variants}>
          <img alt={'Hand 2'} src={`${iconsPath}hand 2.svg`} />
        </Hand2Layout>
        <Hand3Layout animate={animationControls} variants={hand3Variants}>
          <img alt={'Hand 3'} src={`${iconsPath}hand 3.svg`} />
        </Hand3Layout>
        <Hand5Layout animate={animationControls} variants={hand5Variants}>
          <img alt={'Hand 5'} src={`${iconsPath}hand 5.svg`} />
        </Hand5Layout>
        <Hand4Layout animate={animationControls} variants={hand4Variants}>
          <img alt={'Hand 4'} src={`${iconsPath}hand 4.svg`} />
        </Hand4Layout>
      </WelcomeMessageContentLayout>
    </WelcomeMessageLayout>
  );
}

const WelcomeMessageContentLayout = styled.div`
  z-index: 1;
  max-width: 37.5rem;
  height: 100%;
  padding-top: 4rem;
  position: relative;
  margin: 0 auto;
  overflow: hidden;
  background-color: ${colors.chimeYellow};

  ${media.tablet`
    height: 37rem;
    border-radius: 24px;
    box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.08), 0 24px 32px 0 rgba(0, 0, 0, 0.04);
  `};
`;

const Star1Layout = styled(motion.div)`
  position: absolute;
  bottom: 20rem;
  left: 7rem;

  ${media.tablet`
    bottom: 20rem;
    left: 14rem;
  `};
`;

const Star2Layout = styled(motion.div)`
  position: absolute;
  bottom: 17rem;
  right: 5rem;

  ${media.tablet`
    bottom: 16rem;
    right: 13rem;
  `};
`;

const Hand1Layout = styled(motion.div)`
  position: absolute;
  bottom: 4rem;
  left: -16rem;

  ${media.tablet`
    bottom: 3rem;
    left: -9rem;
  `};
`;

const Hand2Layout = styled(motion.div)`
  position: absolute;
  bottom: -6rem;
  left: -7rem;

  ${media.tablet`
    bottom: -7rem;
    left: -1rem;
  `};
`;

const Hand3Layout = styled(motion.div)`
  position: absolute;
  bottom: -9rem;
  right: 0;

  ${media.tablet`
    right: 7rem;
  `}
`;

const Hand4Layout = styled(motion.div)`
  position: absolute;
  bottom: -10rem;
  right: -10rem;

  ${media.tablet`
    bottom: -9rem;
    right: -3rem;
  `};
`;

const Hand5Layout = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  right: -15rem;

  ${media.tablet`
    bottom: 2rem;
    right: -8rem;
  `};
`;

const WelcomeMessageLayout = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: ${colors.lightYellow};

  ${media.tablet`
    padding-top: 65px;
  `}
`;
const TitleLayout = styled(motion.div)`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

export default WelcomeMessage;
